import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Chip, Tooltip, Skeleton, Switch, FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import BlockIcon from "@mui/icons-material/Block";
import swal from "sweetalert";
import axios from "axios";
import api from "../components/utils/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ["#ED9519", "#60a5fa", "#a78bfa", "#4ade80", "#f87171", "#34d399"];

function getAvatarColor(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name = "") {
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";
}

function getUserStatus(user) {
  if (!user.habilitado && user.hasFirma) return { label: "Inactivo",     color: "#666",    bg: "rgba(100,100,100,0.12)", Icon: BlockIcon };
  if (!user.hasPassword)                 return { label: "Invitado",     color: "#fbbf24", bg: "rgba(251,191,36,0.12)",  Icon: MarkEmailUnreadOutlinedIcon };
  if (!user.hasFirma)                    return { label: "Pend. firma",  color: "#60a5fa", bg: "rgba(96,165,250,0.12)",  Icon: AccessTimeIcon };
  return                                        { label: "Activo",       color: "#4ade80", bg: "rgba(74,222,128,0.12)",  Icon: CheckCircleOutlineIcon };
}

// ─── Componente principal ──────────────────────────────────────────────────────
export default function GestionUsuarios() {
  const userToken = localStorage.getItem("user-token");
  const headers = { "iron-token": userToken };

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [toggling, setToggling] = useState(null); // id del usuario que se está toggleando

  // Modal crear
  const [open, setOpen]             = useState(false);
  const [nombreCompleto, setNombre] = useState("");
  const [email, setEmail]           = useState("");
  const [rol, setRol]               = useState("user");
  const [creating, setCreating]     = useState(false);
  const [formError, setFormError]   = useState("");

  // Modal editar
  const [editOpen, setEditOpen]       = useState(false);
  const [editUser, setEditUser]       = useState(null);
  const [editNombre, setEditNombre]   = useState("");
  const [editEmail, setEditEmail]     = useState("");
  const [editRol, setEditRol]         = useState("user");
  const [editError, setEditError]     = useState("");
  const [saving, setSaving]           = useState(false);

  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(api.urlAPI + "usuarios", { headers });
      setUsuarios(res.data);
    } catch {
      // sin conexión o error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);

  const openEdit = (user) => {
    setEditUser({ ...user });
    setEditNombre(user.nombreCompleto || "");
    setEditEmail(user.email || "");
    setEditRol(user.roles?.[0]?.name?.toLowerCase() === "admin" ? "admin" : "user");
    setEditError("");
    setEditOpen(true);
  };

  const handleEliminarFirma = async () => {
    const ok = await swal({
      title: "¿Eliminar firma?",
      text: `${editUser?.nombreCompleto} deberá registrar una nueva firma la próxima vez que inicie sesión.`,
      icon: "warning", buttons: ["Cancelar", "Sí, eliminar"], dangerMode: true,
    });
    if (!ok) return;
    try {
      await axios.delete(api.urlAPI + `usuarios/${editUser.id}/firma`, { headers });
      setEditOpen(false);
      await fetchUsuarios();
      swal("Listo", "Firma eliminada. El usuario deberá registrar una nueva al ingresar.", "success");
    } catch (err) {
      setEditError(err.response?.data?.message || "Error al eliminar la firma.");
    }
  };

  const handleResetPassword = async () => {
    const ok = await swal({
      title: "¿Blanquear contraseña?",
      text: `Se enviará un email a ${editUser?.email} con un enlace para crear una nueva contraseña.`,
      icon: "warning", buttons: ["Cancelar", "Sí, enviar"], dangerMode: false,
    });
    if (!ok) return;
    try {
      const res = await axios.post(api.urlAPI + `usuarios/${editUser.id}/reset-password`, {}, { headers });
      setEditOpen(false);
      swal("Email enviado", res.data.message, "success");
    } catch (err) {
      setEditError(err.response?.data?.message || "Error al enviar el email.");
    }
  };

  const handleEditar = async () => {
    setEditError("");
    if (!editNombre.trim()) return setEditError("El nombre es obligatorio.");
    if (!editEmail.trim() || !editEmail.includes("@")) return setEditError("Email inválido.");
    try {
      setSaving(true);
      await axios.patch(
        api.urlAPI + `usuarios/${editUser.id}`,
        { nombreCompleto: editNombre, email: editEmail, rol: editRol },
        { headers }
      );
      setEditOpen(false);
      await fetchUsuarios();
    } catch (err) {
      setEditError(err.response?.data?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (user) => {
    setToggling(user.id);
    try {
      const res = await axios.patch(
        api.urlAPI + `usuarios/${user.id}/habilitado`,
        {},
        { headers }
      );
      setUsuarios(prev =>
        prev.map(u => u.id === user.id ? { ...u, habilitado: res.data.habilitado } : u)
      );
    } catch {
      swal("Error", "No se pudo cambiar el estado.", "error");
    } finally {
      setToggling(null);
    }
  };

  const handleToggleMailFlag = async (user, flag) => {
    try {
      const res = await axios.patch(
        api.urlAPI + `usuarios/${user.id}/mail/${flag}`,
        {},
        { headers }
      );
      setUsuarios(prev =>
        prev.map(u => u.id === user.id ? { ...u, [flag]: res.data[flag] } : u)
      );
    } catch {
      swal("Error", "No se pudo cambiar la preferencia de email.", "error");
    }
  };

  const handleCrear = async () => {
    setFormError("");
    if (!nombreCompleto.trim()) return setFormError("El nombre es obligatorio.");
    if (!email.trim() || !email.includes("@")) return setFormError("Ingresá un email válido.");
    try {
      setCreating(true);
      await axios.post(api.urlAPI + "usuarios", { nombreCompleto, email, rol }, { headers });
      setOpen(false);
      setNombre(""); setEmail(""); setRol("user");
      await fetchUsuarios();
      swal("¡Listo!", "Usuario creado. Se envió la invitación por email.", "success");
    } catch (err) {
      setFormError(err.response?.data?.message || "Error al crear el usuario.");
    } finally {
      setCreating(false);
    }
  };

  const activeCount  = usuarios.filter(u => u.firma && u.habilitado).length;
  const pendingCount = usuarios.filter(u => !u.firma).length;

  return (
    <Box sx={{ p: 2, bgcolor: "background.default", minHeight: "calc(100vh - 56px)" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}>
            Gestión de usuarios
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Solo visible para administradores
          </Typography>
        </Box>
        <Button
          onClick={() => setOpen(true)}
          startIcon={<AddIcon />}
          sx={{
            bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 600,
            borderRadius: 2, px: 2, py: 0.9, fontSize: 13,
            "&:hover": { bgcolor: "#d4851a" },
          }}
        >
          Nuevo
        </Button>
      </Box>

      {/* ── KPIs rápidos ── */}
      <Box sx={{ display: "flex", gap: 1.2, mb: 2.5 }}>
        {[
          { label: "Total",    value: usuarios.length, color: "text.primary" },
          { label: "Activos",  value: activeCount,     color: "#4ade80"      },
          { label: "Pendientes", value: pendingCount,  color: "#fbbf24"      },
        ].map(({ label, value, color }) => (
          <Box key={label} sx={{
            flex: 1, bgcolor: "background.paper",
            border: "1px solid", borderColor: "divider",
            borderRadius: 2.5, p: "8px 12px",
          }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>
              {loading ? <Skeleton width={28} /> : value}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Lista ── */}
      {loading
        ? [1, 2, 3].map(i => (
            <Box key={i} sx={{
              bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
              borderRadius: 2.5, p: "12px 14px", mb: 1,
              display: "flex", alignItems: "center", gap: 1.5,
            }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="55%" height={16} />
                <Skeleton width="40%" height={13} sx={{ mt: 0.5 }} />
              </Box>
              <Skeleton width={60} height={28} sx={{ borderRadius: 99 }} />
            </Box>
          ))
        : usuarios.length === 0
          ? (
            <Box sx={{
              bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
              borderRadius: 2.5, p: 4, textAlign: "center",
            }}>
              <PersonOutlineIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                No hay usuarios registrados todavía.
              </Typography>
            </Box>
          )
          : usuarios.map(user => {
            const color  = getAvatarColor(user.nombreCompleto || "");
            const initials = getInitials(user.nombreCompleto || "");
            const roleLabel = user.roles?.[0]?.name === "admin" ? "Admin" : "Usuario";
            const isAdmin = user.roles?.[0]?.name === "admin";
            const status = getUserStatus(user);
            const StatusIcon = status.Icon;

            return (
              <Box key={user.id} sx={{
                bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
                borderRadius: 2.5, p: "12px 14px", mb: 1,
                display: "flex", alignItems: "center", gap: 1.5,
              }}>
                {/* Avatar */}
                <Box sx={{
                  width: 40, height: 40, borderRadius: "50%",
                  bgcolor: `${color}22`, border: `1.5px solid ${color}44`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color }}>
                    {initials}
                  </Typography>
                </Box>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: "text.primary",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user.nombreCompleto}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                    {user.email}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.5 }}>
                    {/* Rol */}
                    <Chip
                      icon={isAdmin ? <AdminPanelSettingsOutlinedIcon sx={{ fontSize: "13px !important" }} /> : <PersonOutlineIcon sx={{ fontSize: "13px !important" }} />}
                      label={roleLabel}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 600,
                        bgcolor: isAdmin ? "rgba(237,149,25,0.12)" : "rgba(96,165,250,0.1)",
                        color: isAdmin ? "#ED9519" : "#60a5fa",
                        "& .MuiChip-icon": { color: "inherit" },
                      }}
                    />
                    {/* Estado */}
                    <Chip
                      icon={<StatusIcon sx={{ fontSize: "12px !important" }} />}
                      label={status.label}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10,
                        bgcolor: status.bg,
                        color: status.color,
                        "& .MuiChip-icon": { color: "inherit" },
                      }}
                    />
                  </Box>
                </Box>

                {/* Editar */}
                <Tooltip title="Editar usuario">
                  <IconButton size="small" onClick={() => openEdit(user)}
                    sx={{ color: "text.secondary", "&:hover": { color: "#ED9519" } }}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Toggle habilitado */}
                <Tooltip title={user.habilitado ? "Deshabilitar" : "Habilitar"}>
                  <Box sx={{ flexShrink: 0 }}>
                    {toggling === user.id
                      ? <CircularProgress size={20} sx={{ color: "#ED9519", m: "10px" }} />
                      : (
                        <Switch
                          checked={!!user.habilitado}
                          onChange={() => handleToggle(user)}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#4ade80" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#4ade80" },
                          }}
                        />
                      )
                    }
                  </Box>
                </Tooltip>
              </Box>
            );
          })
      }

      {/* ── Modal editar usuario ── */}
      <Dialog
        open={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            border: "1px solid", borderColor: "divider",
            borderRadius: 3, width: "100%", maxWidth: 420, m: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>
          Editar usuario
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          {editError && (
            <Typography sx={{ fontSize: 12, color: "error.main", mb: 1.5 }}>
              {editError}
            </Typography>
          )}
          <TextField
            fullWidth label="Nombre completo" value={editNombre}
            onChange={e => setEditNombre(e.target.value)}
            sx={{ mb: 1.5 }} disabled={saving}
          />
          <TextField
            fullWidth label="Email" type="email" value={editEmail}
            onChange={e => setEditEmail(e.target.value)}
            sx={{ mb: 1.5 }} disabled={saving}
          />
          <FormControl fullWidth disabled={saving}>
            <InputLabel>Rol</InputLabel>
            <Select value={editRol} label="Rol" onChange={e => setEditRol(e.target.value)}>
              <MenuItem value="user">Usuario</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
            </Select>
          </FormControl>

          {/* Notificaciones por email */}
          <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" sx={{ color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 1 }}>
              Notificaciones por email
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!editUser?.recibeMailHoras}
                  onChange={() => {
                    const updated = { ...editUser, recibeMailHoras: !editUser?.recibeMailHoras };
                    setEditUser(updated);
                    handleToggleMailFlag(editUser, "recibeMailHoras");
                  }}
                  size="small"
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#ED9519" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#ED9519" },
                  }}
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>Recibe mail de horas (admin)</Typography>}
              sx={{ mb: 0.5 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!editUser?.recibeMailReporte}
                  onChange={() => {
                    const updated = { ...editUser, recibeMailReporte: !editUser?.recibeMailReporte };
                    setEditUser(updated);
                    handleToggleMailFlag(editUser, "recibeMailReporte");
                  }}
                  size="small"
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#ED9519" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#ED9519" },
                  }}
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>Recibe PDF de reporte de servicio</Typography>}
            />
          </Box>

          {/* Acciones destructivas */}
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" sx={{ color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 1 }}>
              Acciones
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                fullWidth size="small"
                onClick={handleResetPassword}
                disabled={saving}
                sx={{
                  border: "1px solid", borderColor: "divider",
                  color: "#fbbf24", borderRadius: 2, fontSize: 12,
                  "&:hover": { bgcolor: "rgba(251,191,36,0.08)", borderColor: "#fbbf24" },
                }}
              >
                Blanquear contraseña
              </Button>
              <Button
                fullWidth size="small"
                onClick={handleEliminarFirma}
                disabled={saving || !editUser?.hasFirma}
                sx={{
                  border: "1px solid", borderColor: "divider",
                  color: "#f87171", borderRadius: 2, fontSize: 12,
                  "&:hover": { bgcolor: "rgba(248,113,113,0.08)", borderColor: "#f87171" },
                  "&.Mui-disabled": { color: "#444", borderColor: "divider" },
                }}
              >
                Eliminar firma
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} disabled={saving}
            sx={{ color: "text.secondary", borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button onClick={handleEditar} disabled={saving}
            sx={{
              bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 600,
              borderRadius: 2, px: 2.5,
              "&:hover": { bgcolor: "#d4851a" },
              "&.Mui-disabled": { bgcolor: "rgba(237,149,25,0.3)", color: "#888" },
            }}>
            {saving ? <CircularProgress size={18} sx={{ color: "#1a0e00" }} /> : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal crear usuario ── */}
      <Dialog
        open={open}
        onClose={() => !creating && setOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            border: "1px solid", borderColor: "divider",
            borderRadius: 3, width: "100%", maxWidth: 420, m: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>
          Nuevo usuario
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
            Se enviará un email con el enlace para crear contraseña.
          </Typography>

          {formError && (
            <Typography sx={{ fontSize: 12, color: "error.main", mb: 1.5 }}>
              {formError}
            </Typography>
          )}

          <TextField
            fullWidth label="Nombre completo" value={nombreCompleto}
            onChange={e => setNombre(e.target.value)}
            sx={{ mb: 1.5 }}
            disabled={creating}
          />
          <TextField
            fullWidth label="Email corporativo" type="email" value={email}
            onChange={e => setEmail(e.target.value)}
            sx={{ mb: 1.5 }}
            disabled={creating}
          />
          <FormControl fullWidth disabled={creating}>
            <InputLabel>Rol</InputLabel>
            <Select value={rol} label="Rol" onChange={e => setRol(e.target.value)}>
              <MenuItem value="user">Usuario</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setOpen(false)} disabled={creating}
            sx={{ color: "text.secondary", borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCrear} disabled={creating}
            sx={{
              bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 600,
              borderRadius: 2, px: 2.5,
              "&:hover": { bgcolor: "#d4851a" },
              "&.Mui-disabled": { bgcolor: "rgba(237,149,25,0.3)", color: "#888" },
            }}
          >
            {creating
              ? <CircularProgress size={18} sx={{ color: "#1a0e00" }} />
              : "Crear y enviar invitación"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
